/**
 * Countdown that randomizes its own end time within a configured hour range,
 * persists it in localStorage so reloads within the same countdown stay consistent,
 * and automatically rolls a new random duration once it reaches zero.
 *
 * Expects child elements marked `data-part="hours"`, `data-part="minutes"`, `data-part="seconds"`.
 *
 * @attr {string} min-hours - Minimum random duration, in hours.
 * @attr {string} max-hours - Maximum random duration, in hours.
 * @attr {string} [storage-key] - localStorage key used to persist the end time.
 */
class RandomCountdownTimer extends HTMLElement {
  /** @type {number | undefined} */
  #intervalId;

  connectedCallback() {
    this.#tick();
    this.#intervalId = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#intervalId);
  }

  get #storageKey() {
    return this.getAttribute('storage-key') || 'icy-promo-countdown-end';
  }

  get #minHours() {
    return Number(this.getAttribute('min-hours')) || 8;
  }

  get #maxHours() {
    return Number(this.getAttribute('max-hours')) || 23;
  }

  /**
   * Reads the persisted end time, generating and storing a new random one if
   * missing, malformed, or already in the past.
   * @returns {number} End time in epoch ms.
   */
  #getOrCreateEndTime() {
    const stored = Number(window.localStorage.getItem(this.#storageKey));

    if (stored && stored > Date.now()) {
      return stored;
    }

    return this.#createNewEndTime();
  }

  /**
   * @returns {number} The newly created end time in epoch ms.
   */
  #createNewEndTime() {
    const minMs = this.#minHours * 3600 * 1000;
    const maxMs = this.#maxHours * 3600 * 1000;
    const durationMs = minMs + Math.random() * (maxMs - minMs);
    const endTime = Date.now() + durationMs;

    window.localStorage.setItem(this.#storageKey, String(endTime));

    return endTime;
  }

  #tick() {
    const endTime = this.#getOrCreateEndTime();
    const remaining = endTime - Date.now();

    if (remaining <= 0) {
      // Expired between reads; roll a new countdown immediately.
      this.#createNewEndTime();
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (/** @type {number} */ n) => String(n).padStart(2, '0');

    this.#setPart('hours', pad(hours));
    this.#setPart('minutes', pad(minutes));
    this.#setPart('seconds', pad(seconds));
  }

  /**
   * @param {string} part
   * @param {string} value
   */
  #setPart(part, value) {
    const element = this.querySelector(`[data-part="${part}"]`);
    if (element) element.textContent = value;
  }
}

if (!customElements.get('random-countdown-timer')) {
  customElements.define('random-countdown-timer', RandomCountdownTimer);
}
